import { BorderTop, Close } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAvailableDates, useGetSlotsWithDisabled, useUpdateDisabledSlots } from '../../../services/core/settings/settings.hooks';
import { CustomTooltip } from '../../../utility/helpers/commonFunction';

// Style objects
const styles = {
    dialog: {
        borderRadius: '8px',
        maxWidth: '900px',
    },
    dialogTitle: {
        height: '64px',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#002394',
        color: 'white',
    },
    titleText: {
        color: '#FFF',
        fontFamily: 'Roboto',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '160%',
        letterSpacing: '0.15px',
    },
    closeButton: {
        color: '#FFF',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
    },
    dialogContent: {
        padding: '24px !important',
        maxHeight: '100vh',
        overflow: 'auto',
    },
    calendarBox: {
        width: { xs: '100%', md: '360px' },
        maxWidth: { xs: '100%', md: '360px' },
    },
    datePicker: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        '& .MuiDateCalendar-root': {
            width: '100%',
            margin: '0',
            maxHeight: '500px!important',
            height: '100%!important',
        },
        '& .MuiPickersLayout-root': {
            padding: '0',
            margin: '0',
            overflow: 'hidden',
        },
        '& .MuiDayCalendar-header': {
            gap: '12px',
        },
        '& .MuiPickersCalendarHeader-root': {
            padding: '8px 0',
            marginBottom: '8px',
        },
        '& .MuiPickersCalendarHeader-label': {
            fontWeight: 600,
            fontSize: '17px',
            letterSpacing: '-0.408px',
            lineHeight: '22px',
            color: '#000',
        },
        '& .MuiDayCalendar-root': {
            overflow: 'hidden',
            width: '100%',
        },
        '& .MuiDayCalendar-weekContainer': {
            margin: '0 0',
            marginBottom: '12px',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
        },
        '& .MuiPickersSlideTransition-root': {
            minHeight: '250px!important',
        },
        '& .MuiPickersDay-root': {
            borderRadius: '50%',
            width: '40px',
            maxWidth: '40px',
            height: '40px',
            fontSize: '15px',
            display: 'flex!important',
            alignItems: 'center!important',
            justifyContent: 'center!important',
            backgroundColor: '#EBF3FC',
            color: '#006CDD',
            fontWeight: 600,
            letterSpacing: '-0.408px',
            margin: '0',
            lineHeight: '22px',
            '&.MuiPickersDay-today': {
                border: '1px solid #006CDD !important',
            },
            '&.Mui-selected': {
                backgroundColor: '#006CDD !important',
                color: '#FFF',
                fontWeight: 500,
                '&:hover': {
                    backgroundColor: '#0052A3',
                },
            },
            '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: '#000',
                opacity: 0.25,
                cursor: 'not-allowed',
            },
            '&:not(.Mui-selected):hover': {
                backgroundColor: '#EBF3FC',
            },
        },
        '& .MuiPickersDay-dayOutsideMonth': {
            color: '#B0B0B0',
        },
        '& .MuiDayCalendar-weekDayLabel': {
            width: '100%',
            maxWidth: '48px',
            flex: 1,
            margin: '0 0',
            color: '#8E8E8E',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '18px',
            letterSpacing: '-0.078px',
            textOverflow: 'clip',
            whiteSpace: 'nowrap',
            '& > span': {
                display: 'inline-block',
                width: '3ch',
                overflow: 'hidden',
            },
        },
        '& *': {
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        },
    },
    selectedDateText: {
        color: '#000',
        fontSize: '17px',
        fontWeight: 600,
        letterSpacing: '-0.408px',
        lineHeight: '22px',
    },
    goToTodayText: {
        color: '#006CDD',
        fontSize: '17px',
        fontWeight: 600,
        letterSpacing: '-0.408px',
        lineHeight: '22px',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            color: '#006CDD',
        },
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
    },
    timeSlotsGrid: {
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
        gap: 1.5,
    },
    emptyStateText: {
        color: '#666',
        textAlign: 'center',
        mt: 4,
    },
    errorStateText: {
        color: '#d32f2f',
        textAlign: 'center',
        mt: 4,
    },
    dialogActions: {
        borderTop: '1px solid #E0E0E0!important',
        padding: '16px 24px',
        justifyContent: 'flex-end',
    },
    baseSlotStyle: {
        borderRadius: '8px',
        padding: '0 16px',
        textTransform: 'none',
        fontSize: '15px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '26px',
        letterSpacing: '-0.46px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'none',
        minHeight: '40px',
        width: '100%',
        '&:hover': {
            backgroundColor: 'inherit',
            border: 'inherit',
        },
    },
    slotStyles: {
        available: {
            backgroundColor: 'transparent',
            color: '#000',
            border: '1.5px solid #E5E8F0',
        },
        disabled: {
            backgroundColor: '#E6E6E6',
            color: 'rgba(0, 0, 0, 0.50)',
            border: '1.5px solid #E6E6E6',
        },
        disabledManually: {
            backgroundColor: '#D32F2F',
            color: '#FFF',
            border: '1.5px solid #D32F2F',
        },
        limitReached: {
            border: '1.5px solid #E6E6E6',
            backgroundColor: '#E6E6E6!important',
            color: 'rgba(0, 0, 0, 0.50)',
            cursor: 'default',
        },
    },
};

const DisabledSlotsModal = ({ open, onClose, dropOffFrequency, startTime, endTime, initialDate }) => {
    const [selectedDate, setSelectedDate] = useState(initialDate ? dayjs(initialDate) : dayjs());
    const [viewedMonth, setViewedMonth] = useState(initialDate ? dayjs(initialDate) : dayjs()); // Track the month being viewed in calendar
    const [timeSlots, setTimeSlots] = useState([]);
    const [originalSlots, setOriginalSlots] = useState([]); // Store original slots from API
    const [showLoader, setShowLoader] = useState(false); // Control minimum loader display time
    const [dateChangeTimestamp, setDateChangeTimestamp] = useState(null); // Track when date changes

    // Get shopId from localStorage
    const shopId = useMemo(() => {
        const shopDetails = JSON.parse(localStorage.getItem('apex-saas_details'));
        return shopDetails?._id;
    }, []);

    // Format date for API
    const formattedDate = useMemo(() => {
        return selectedDate.format('YYYY-MM-DD');
    }, [selectedDate]);

    // Extract year and month from viewedMonth for available dates API (not selectedDate)
    const selectedYear = useMemo(() => viewedMonth.year(), [viewedMonth]);
    const selectedMonth = useMemo(() => viewedMonth.month() + 1, [viewedMonth]); // dayjs months are 0-indexed, API expects 1-12

    // Get available dates for the current month/year
    const availableDatesQuery = useGetAvailableDates(
        { shopId, year: selectedYear.toString(), month: selectedMonth.toString() },
        { enabled: open && !!shopId && !!selectedYear && !!selectedMonth } // Only fetch when modal is open
    );

    // Process available dates to create a Set of available date strings (YYYY-MM-DD format)
    const availableDatesSet = useMemo(() => {
        if (availableDatesQuery.isSuccess && availableDatesQuery.data && Array.isArray(availableDatesQuery.data)) {
            return new Set(availableDatesQuery.data.filter((dateObj) => dateObj.isAvailable).map((dateObj) => dayjs(dateObj.date).format('YYYY-MM-DD')));
        }
        return new Set();
    }, [availableDatesQuery.isSuccess, availableDatesQuery.data]);

    // Track if the available dates query has successfully loaded
    // This helps us determine if we should disable dates when data is empty
    const hasAvailableDatesData = availableDatesQuery.isSuccess;

    // Get array of available dates sorted by date (only today and future dates)
    const availableDatesArray = useMemo(() => {
        if (availableDatesQuery.isSuccess && availableDatesQuery.data && Array.isArray(availableDatesQuery.data)) {
            const today = dayjs().startOf('day'); // Get today at start of day for comparison
            return availableDatesQuery.data
                .filter((dateObj) => dateObj.isAvailable)
                .map((dateObj) => {
                    const date = dayjs(dateObj.date);
                    return date;
                })
                .filter((date) => {
                    if (!date || !date.isValid()) {
                        return false;
                    }
                    // Use isAfter or isSame instead of isSameOrAfter
                    const isTodayOrFuture = date.isAfter(today, 'day') || date.isSame(today, 'day');

                    return isTodayOrFuture;
                })
                .sort((a, b) => a.valueOf() - b.valueOf()); // Sort chronologically
        }
        return [];
    }, [availableDatesQuery.isSuccess, availableDatesQuery.data]);

    // Check if the currently selected date is available
    const isSelectedDateAvailable = useMemo(() => {
        if (!hasAvailableDatesData) return true; // Allow while loading
        return availableDatesSet.has(formattedDate);
    }, [hasAvailableDatesData, availableDatesSet, formattedDate]);

    // Check if the back button (left arrow) should be disabled
    // Disable it when viewing current month or past months (since past dates are disabled)
    const isBackButtonDisabled = useMemo(() => {
        const currentMonth = dayjs().month(); // 0-11
        const currentYear = dayjs().year();
        const viewedMonthIndex = viewedMonth.month(); // 0-11
        const viewedYear = viewedMonth.year();

        // Disable if viewing current month or past months
        if (viewedYear < currentYear) {
            return true; // Past year
        }
        if (viewedYear === currentYear && viewedMonthIndex <= currentMonth) {
            return true; // Current month or past month in current year
        }
        return false; // Future month
    }, [viewedMonth]);

    // Hook for getting slots with disabled status (now uses useQuery with caching)
    // Only fetch slots if the selected date is available
    const slotsQuery = useGetSlotsWithDisabled(
        { shopId, date: formattedDate },
        { enabled: open && !!shopId && !!formattedDate && isSelectedDateAvailable } // Only fetch when modal is open and date is available
    );

    // Hook for updating disabled slots
    const updateDisabledSlotsMutation = useUpdateDisabledSlots();

    // Reset to initialDate or today's date when modal opens
    useEffect(() => {
        if (open) {
            const dateToSet = initialDate ? dayjs(initialDate) : dayjs();
            setSelectedDate(dateToSet);
            setViewedMonth(dateToSet); // Set viewed month to match initial date
            setTimeSlots([]);
            setOriginalSlots([]);
            setShowLoader(true);
            setDateChangeTimestamp(Date.now());
        } else {
            // Reset to today's date when modal closes
            const today = dayjs();
            setSelectedDate(today);
            setViewedMonth(today);
        }
    }, [open, initialDate]);

    // Auto-select first available date when month changes
    useEffect(() => {
        // Only run when modal is open and available dates are loaded
        if (open && hasAvailableDatesData) {
            // Check if current selected date is in the viewed month and is available
            const isCurrentDateInViewedMonth = selectedDate.year() === selectedYear && selectedDate.month() + 1 === selectedMonth;
            const isCurrentDateAvailable = availableDatesSet.has(selectedDate.format('YYYY-MM-DD'));

            if (availableDatesArray.length > 0) {
                // If current date is not in viewed month or not available, select first available date
                if (!isCurrentDateInViewedMonth || !isCurrentDateAvailable) {
                    const firstAvailableDateInViewedMonth = availableDatesArray.find((date) => {
                        return date.year() === selectedYear && date.month() + 1 === selectedMonth;
                    });
                    if (firstAvailableDateInViewedMonth) {
                        setSelectedDate(firstAvailableDateInViewedMonth);
                    }
                }
            } else {
                // If no available dates in the month, clear slots
                setTimeSlots([]);
                setOriginalSlots([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, selectedMonth, hasAvailableDatesData]); // Only trigger when month/year changes

    // Track date changes and show loader for minimum time (200ms)
    useEffect(() => {
        if (open && formattedDate) {
            const timestamp = Date.now();
            setShowLoader(true);
            setDateChangeTimestamp(timestamp);
        }
    }, [formattedDate, open]);

    // Hide loader when query completes (after minimum 200ms has passed)
    useEffect(() => {
        if (dateChangeTimestamp && !slotsQuery.isLoading && (slotsQuery.isSuccess || slotsQuery.isError)) {
            const elapsed = Date.now() - dateChangeTimestamp;
            if (elapsed >= 350) {
                setShowLoader(false);
            } else {
                // Wait for remaining time to ensure minimum 200ms display
                const remainingTime = 350 - elapsed;
                const timer = setTimeout(() => {
                    setShowLoader(false);
                }, remainingTime);
                return () => clearTimeout(timer);
            }
        }
    }, [slotsQuery.isLoading, slotsQuery.isSuccess, slotsQuery.isError, dateChangeTimestamp]);

    // Process slots from API response (no filtering - store all slots)
    // useQuery automatically handles caching, so we don't need manual date tracking
    useEffect(() => {
        // If selected date is not available, clear slots
        if (hasAvailableDatesData && !isSelectedDateAvailable) {
            setTimeSlots([]);
            setOriginalSlots([]);
            return;
        }

        if (slotsQuery.isSuccess && slotsQuery.data && open && isSelectedDateAvailable) {
            const responseData = slotsQuery.data;

            // Response structure: { date: "2025-10-30", slots: [...] }
            if (responseData && Array.isArray(responseData) && responseData.length > 0) {
                const processedSlots = responseData.map((slot) => {
                    const startDateTime = dayjs(slot.startDateTime);

                    return {
                        time: startDateTime.format('h:mm A'),
                        value: startDateTime.format('HH:mm'),
                        startDateTime: slot.startDateTime,
                        endDateTime: slot.endDateTime,
                        availability: slot.availability !== undefined ? slot.availability : true,
                        isLimitReached: slot.isLimitReached || false,
                    };
                });

                // Store original slots and processed slots (all slots, no filtering)
                setOriginalSlots(processedSlots);
                setTimeSlots(processedSlots);
            } else {
                setTimeSlots([]);
                setOriginalSlots([]);
            }
        } else if (slotsQuery.isError) {
            setTimeSlots([]);
            setOriginalSlots([]);
        }
    }, [slotsQuery.isSuccess, slotsQuery.data, slotsQuery.isError, open, hasAvailableDatesData, isSelectedDateAvailable]);

    const handleSlotClick = (slot) => {
        // If limit reached, don't allow toggling
        const isLimitReached = slot.availability ? slot.isLimitReached : false;
        if (isLimitReached) return;

        // Toggle availability for the clicked slot
        const updatedSlots = timeSlots.map((s) => {
            if (s.value === slot.value) {
                return {
                    ...s,
                    availability: !s.availability,
                };
            }
            return s;
        });
        setTimeSlots(updatedSlots);
    };

    const handleSave = () => {
        // Prepare the payload with date and slots array
        const formattedDate = selectedDate.format('YYYY-MM-DD');
        const payload = {
            date: formattedDate,
            slots: timeSlots.map((slot) => ({
                startDateTime: slot.startDateTime,
                endDateTime: slot.endDateTime,
                isLimitReached: slot.isLimitReached,
                availability: slot.availability,
            })),
        };

        // Call API to save disabled slots
        updateDisabledSlotsMutation.mutate(payload, {
            onSuccess: () => {
                onClose();
            },
        });
    };

    const handleCancel = () => {
        // Reset to original slots from API
        setTimeSlots(originalSlots);
        onClose();
    };

    const handleGoToToday = () => {
        const today = dayjs();
        setSelectedDate(today);
    };

    // Check if there are any manually disabled slots (not auto-disabled due to limit)
    const hasManuallyDisabledSlots = useMemo(() => {
        return timeSlots.some((slot) => !slot.availability && !slot.isLimitReached);
    }, [timeSlots]);

    // Handler to enable all manually disabled slots
    const handleEnableAllSlots = () => {
        const updatedSlots = timeSlots.map((slot) => {
            // Only enable manually disabled slots (not limit-reached slots)
            const isLimitReached = slot.availability ? slot.isLimitReached : false;
            if (!isLimitReached) {
                return {
                    ...slot,
                    availability: true,
                };
            }
            return slot;
        });
        setTimeSlots(updatedSlots);
    };

    const getSlotTooltip = (slot) => {
        const isLimitReached = slot.isLimitReached;
        const availability = slot.availability;
        if (!availability) {
            return {
                title: (
                    <>
                        <Typography
                            sx={{
                                color: '#212121',
                                fontSize: '14px!important',
                                fontWeight: 700,
                                lineHeight: '140%',
                                letterSpacing: '-0.42px',
                                marginBottom: '0px',
                                textAlign: 'center',
                            }}
                        >
                            Manually disabled
                        </Typography>
                        <Typography
                            sx={{
                                color: '#212121',
                                fontSize: '14px!important',
                                fontWeight: 400,
                                lineHeight: '140%',
                                letterSpacing: '-0.42px',
                                marginBottom: '0px',
                                textAlign: 'center',
                            }}
                        >
                            Click on the slot to re-enable.
                        </Typography>
                    </>
                ),
            };
        }
        if (isLimitReached) {
            return {
                title: (
                    <>
                        <Typography
                            sx={{
                                color: '#212121',
                                fontSize: '14px',
                                fontWeight: 700,
                                lineHeight: '140%',
                                letterSpacing: '-0.42px',
                                marginBottom: '0px',
                                textAlign: 'center',
                            }}
                        >
                            Slot automatically disabled
                        </Typography>
                        <Typography
                            sx={{
                                color: '#212121',
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '140%',
                                letterSpacing: '-0.42px',
                                marginBottom: '0px',
                                textAlign: 'center',
                            }}
                        >
                            Maximum drop-offs reached.
                        </Typography>
                    </>
                ),
            };
        }

        return null;
    };

    const getSlotStyle = (slot) => {
        const isLimitReached = slot.isLimitReached;
        const availability = slot.availability;

        // Priority: availability > limitReached
        if (!availability) {
            // Red color for disabled slots (availability: false)
            return { ...styles.baseSlotStyle, ...styles.slotStyles.disabledManually };
        }
        if (isLimitReached) {
            // Grey color for limit reached slots
            return { ...styles.baseSlotStyle, ...styles.slotStyles.limitReached };
        }

        // Available slots (availability: true)
        return { ...styles.baseSlotStyle, ...styles.slotStyles.available };
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth PaperProps={{ sx: styles.dialog }}>
            <DialogTitle sx={styles.dialogTitle}>
                <Typography sx={styles.titleText}>Select a Drop-off Date and Time to Disable</Typography>
                <IconButton onClick={handleCancel} size="small" sx={styles.closeButton}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={styles.dialogContent}>
                <Grid container spacing={6}>
                    <Grid
                        item
                        xs={12}
                        md="auto"
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            display: 'flex',
                            justifyContent: { xs: 'center', md: 'flex-start' },
                        }}
                    >
                        <Box sx={{ ...styles.calendarBox, position: 'relative' }}>
                            {availableDatesQuery.isLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 10,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                            <Stack>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale={{
                                        formats: {
                                            dayOfMonth: 'D',
                                            monthAndYear: 'MMMM YYYY',
                                        },
                                    }}
                                >
                                    <StaticDatePicker
                                        displayStaticWrapperAs="desktop"
                                        value={selectedDate}
                                        onChange={(newValue) => setSelectedDate(newValue)}
                                        dayOfWeekFormatter={(_day, date) => date.format('ddd').toUpperCase()}
                                        minDate={dayjs().startOf('month')} // Prevent navigation to past months
                                        shouldDisableDate={(date) => {
                                            // Disable past dates
                                            if (date.isBefore(dayjs(), 'day')) {
                                                return true;
                                            }
                                            // If the query is still loading for the current month, disable all dates by default
                                            if (availableDatesQuery.isLoading) {
                                                return true; // Disable all dates while loading
                                            }
                                            // If the available dates query has successfully loaded
                                            if (hasAvailableDatesData) {
                                                const dateStr = date.format('YYYY-MM-DD');
                                                // If data is empty (no available dates), disable all dates
                                                // If data exists, only enable dates that are in the available set
                                                if (availableDatesSet.size === 0 || !availableDatesSet.has(dateStr)) {
                                                    return true;
                                                }
                                                // Date is available, enable it
                                                return false;
                                            }
                                            // If query hasn't loaded yet (initial state), disable all dates by default
                                            return true;
                                        }}
                                        onMonthChange={(newValue) => {
                                            // Prevent navigation to past months
                                            const currentMonth = dayjs().startOf('month');
                                            if (newValue.isBefore(currentMonth, 'month')) {
                                                return; // Don't update if trying to go to past month
                                            }
                                            // When month changes, update viewedMonth to trigger API refetch
                                            // This doesn't change the selected date, only the viewed month
                                            setViewedMonth(newValue);
                                        }}
                                        slotProps={{
                                            leftArrowIcon: {
                                                fontSize: 'medium',
                                                sx: {
                                                    color: isBackButtonDisabled ? '#ccc' : '#000',
                                                    cursor: isBackButtonDisabled ? 'not-allowed' : 'pointer',
                                                    opacity: isBackButtonDisabled ? 0.5 : 1,
                                                },
                                            },
                                            rightArrowIcon: {
                                                fontSize: 'medium',
                                                sx: { color: '#000' },
                                            },
                                        }}
                                        sx={styles.datePicker}
                                    />
                                </LocalizationProvider>

                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mt: 2, pl: 1 }}>
                                    <Typography variant="body2" sx={styles.selectedDateText}>
                                        You selected {selectedDate.format('M/D/YYYY')}
                                    </Typography>
                                    <Typography component="span" onClick={handleGoToToday} sx={styles.goToTodayText}>
                                        Go to Today
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md sx={{ flex: 1, minWidth: 0 }}>
                        <Stack sx={{ height: '100%' }}>
                            {showLoader || slotsQuery.isLoading || availableDatesQuery.isLoading ? (
                                <Box sx={styles.loadingBox}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box sx={styles.timeSlotsGrid}>
                                    {timeSlots
                                        .filter((slot) => {
                                            // Filter out past slots only for display (if selected date is today)
                                            const now = new Date();
                                            const selectedDateStr = selectedDate.format('YYYY-MM-DD');
                                            const todayStr = dayjs().format('YYYY-MM-DD');
                                            const isToday = selectedDateStr === todayStr;

                                            if (isToday) {
                                                // Get the slot time from startDateTime
                                                const startDateTime = dayjs(slot.startDateTime);
                                                const slotTime = startDateTime.format('HH:mm');

                                                // Combine selected date with slot time
                                                const combinedDateTimeStr = `${selectedDateStr} ${slotTime}`;
                                                const combinedDateTime = dayjs(combinedDateTimeStr, 'YYYY-MM-DD HH:mm').toDate();

                                                // Check if combined datetime is greater than current time
                                                return combinedDateTime > now;
                                            }

                                            // For future dates, show all slots
                                            return true;
                                        })
                                        .map((slot, index) => {
                                            const isLimitReached = slot.availability ? slot.isLimitReached : false;
                                            const tooltipData = getSlotTooltip(slot);
                                            const slotStyle = getSlotStyle(slot);

                                            const slotButton = (
                                                <Button
                                                    key={index}
                                                    variant="outlined"
                                                    onClick={() => handleSlotClick(slot)}
                                                    disabled={isLimitReached}
                                                    sx={{
                                                        ...slotStyle,
                                                        '&:hover': {
                                                            backgroundColor: slotStyle.backgroundColor,
                                                            color: slotStyle.color,
                                                            border: slotStyle.border,
                                                        },
                                                        '&:focus': {
                                                            backgroundColor: slotStyle.backgroundColor,
                                                            color: slotStyle.color,
                                                            border: slotStyle.border,
                                                        },
                                                    }}
                                                >
                                                    {slot.time}
                                                </Button>
                                            );

                                            return tooltipData ? (
                                                <CustomTooltip
                                                    key={index}
                                                    width="210px"
                                                    placement="top"
                                                    title={tooltipData.title}
                                                    PopperProps={{
                                                        sx: {
                                                            pointerEvents: 'none', // Disable pointer events on tooltip
                                                        },
                                                    }}
                                                >
                                                    <Box>{slotButton}</Box>
                                                </CustomTooltip>
                                            ) : (
                                                slotButton
                                            );
                                        })}
                                </Box>
                            )}

                            {!slotsQuery.isLoading && slotsQuery.isSuccess && timeSlots.length === 0 && (
                                <Typography variant="body2" sx={styles.emptyStateText}>
                                    No time slots available for this date.
                                </Typography>
                            )}
                            {!slotsQuery.isLoading && slotsQuery.isError && (
                                <Typography variant="body2" sx={styles.errorStateText}>
                                    Error loading slots. Please try again.
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={styles.dialogActions}>
                {hasManuallyDisabledSlots && (
                    <Button
                        variant="contained"
                        color="greyColor"
                        onClick={handleEnableAllSlots}
                        sx={{
                            marginRight: 'auto',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.25)',
                            letterSpacing: '0.4px',
                            color: '#000',
                            '&:hover': {
                                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
                            },
                        }}
                        disabled={updateDisabledSlotsMutation.isPending}
                    >
                        ENABLE ALL SLOTS
                    </Button>
                )}
                <Button variant="outlined" onClick={handleCancel} sx={{ minWidth: '100px' }} disabled={updateDisabledSlotsMutation.isPending}>
                    CANCEL
                </Button>
                <Button variant="contained" onClick={handleSave} sx={{ minWidth: '100px' }} disabled={updateDisabledSlotsMutation.isPending}>
                    {updateDisabledSlotsMutation.isPending ? 'SAVING...' : 'SAVE'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DisabledSlotsModal;
